/**
 * useTeamUsers
 *
 * Fetches company users with presence (online/offline) and keeps the list
 * updated in real-time via socket events (TEAM_USER_ONLINE / TEAM_USER_OFFLINE).
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import { SOCKET_EVENTS } from '@troia-v3/types';
import type { TeamUserOnlineEvent, TeamUserOfflineEvent, TeamUserWithPresence } from '@troia-v3/types';
import { teamChatService } from '../services/teamChatService';

export const useTeamUsers = () => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  const query = useQuery({
    queryKey: ['team-chat', 'users'],
    queryFn: () => teamChatService.listUsers(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // Real-time presence updates via socket
  useEffect(() => {
    if (!socket) return;

    const handleOnline = (event: TeamUserOnlineEvent) => {
      queryClient.setQueryData<TeamUserWithPresence[]>(['team-chat', 'users'], (old) => {
        if (!old) return old;
        return old
          .map((u) => (u.id === event.userId ? { ...u, isOnline: true } : u))
          .sort((a, b) => {
            if (a.isOnline && !b.isOnline) return -1;
            if (!a.isOnline && b.isOnline) return 1;
            return a.firstName.localeCompare(b.firstName);
          });
      });
    };

    const handleOffline = (event: TeamUserOfflineEvent) => {
      queryClient.setQueryData<TeamUserWithPresence[]>(['team-chat', 'users'], (old) => {
        if (!old) return old;
        return old
          .map((u) =>
            u.id === event.userId ? { ...u, isOnline: false, lastSeen: event.lastSeen } : u
          )
          .sort((a, b) => {
            if (a.isOnline && !b.isOnline) return -1;
            if (!a.isOnline && b.isOnline) return 1;
            return a.firstName.localeCompare(b.firstName);
          });
      });
    };

    socket.on(SOCKET_EVENTS.TEAM_USER_ONLINE, handleOnline);
    socket.on(SOCKET_EVENTS.TEAM_USER_OFFLINE, handleOffline);

    return () => {
      socket.off(SOCKET_EVENTS.TEAM_USER_ONLINE, handleOnline);
      socket.off(SOCKET_EVENTS.TEAM_USER_OFFLINE, handleOffline);
    };
  }, [socket, queryClient]);

  return query;
};
